import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import FormProgressIndicator from '../../problem-statement-submission/components/FormProgressIndicator';
import ProblemOverviewSection from '../../problem-statement-submission/components/ProblemOverviewSection';
import RequirementsSection from '../../problem-statement-submission/components/RequirementsSection';
import StakeholdersSection from '../../problem-statement-submission/components/StakeholdersSection';
import TimelineSection from '../../problem-statement-submission/components/TimelineSection';
import FileUploadSection from '../../problem-statement-submission/components/FileUploadSection';
import PreviewSection from '../../problem-statement-submission/components/PreviewSection';
import problemStatementService from '../../../services/problemStatementService';
import { useAuth } from '../../../contexts/AuthContext';

const SubmitProblemModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  
  const [formData, setFormData] = useState({
    // Problem Overview
    title: '',
    category: '',
    customCategory: '',
    priority: '',
    complexity: '',
    description: '',
    background: '',
    affectedPopulation: '',
    
    // Requirements
    objectives: [],
    constraints: [],
    expectedDeliverables: [],
    successMetrics: [],
    budgetRange: '',
    resourceAvailability: '',
    
    // Stakeholders
    primaryContact: {
      name: '',
      title: '',
      department: '',
      email: '',
      phone: '',
      availability: ''
    },
    stakeholders: [],
    communicationPreferences: [],
    meetingFrequency: '',
    preferredMeetingTime: '',
    
    // Timeline
    projectDuration: '',
    urgencyLevel: '',
    projectStartDate: '',
    projectEndDate: '',
    customDuration: '',
    milestones: [],
    seasonalConsiderations: '',
    flexibilityRequirements: '',
    
    // Files
    attachments: []
  });

  const totalSteps = 5;
  const stepTitles = ['Overview', 'Requirements', 'Stakeholders', 'Timeline', 'Review'];

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setCurrentStep(1);
      setShowPreview(false);
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData?.title?.trim()) newErrors.title = 'Title is required';
      if (!formData?.category) newErrors.category = 'Category is required';
      if (formData?.category === 'other' && !formData?.customCategory?.trim()) {
        newErrors.customCategory = 'Custom category is required';
      }
      if (!formData?.priority) newErrors.priority = 'Priority is required';
      if (!formData?.complexity) newErrors.complexity = 'Complexity is required';
      if (!formData?.description?.trim()) newErrors.description = 'Description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowPreview(true);
      }
    }
  };

  const handleBack = () => {
    if (showPreview) {
      setShowPreview(false);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) {
      setCurrentStep(1);
      setShowPreview(false);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await problemStatementService?.createProblemStatement(formData, user?.id);
      
      // Success - close modal and notify parent
      onSuccess?.();
      onClose();
      
      // Reset form
      setFormData({
        title: '',
        category: '',
        customCategory: '',
        priority: '',
        complexity: '',
        description: '',
        background: '',
        affectedPopulation: '',
        objectives: [],
        constraints: [],
        expectedDeliverables: [],
        successMetrics: [],
        budgetRange: '',
        resourceAvailability: '',
        primaryContact: {
          name: '',
          title: '',
          department: '',
          email: '',
          phone: '',
          availability: ''
        },
        stakeholders: [],
        communicationPreferences: [],
        meetingFrequency: '',
        preferredMeetingTime: '',
        projectDuration: '',
        urgencyLevel: '',
        projectStartDate: '',
        projectEndDate: '',
        customDuration: '',
        milestones: [],
        seasonalConsiderations: '',
        flexibilityRequirements: '',
        attachments: []
      });
    } catch (error) {
      console.error('Error submitting problem statement:', error);
      setSubmitError(error?.message || 'Failed to submit problem statement. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    if (showPreview) {
      return <PreviewSection data={formData} onEdit={() => setShowPreview(false)} />;
    }

    switch (currentStep) {
      case 1:
        return (
          <ProblemOverviewSection
            data={formData}
            onChange={handleFieldChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <RequirementsSection
            data={formData}
            onChange={handleFieldChange}
            errors={errors}
          />
        );
      case 3:
        return (
          <StakeholdersSection
            data={formData}
            onChange={handleFieldChange}
            errors={errors}
          />
        );
      case 4:
        return (
          <TimelineSection
            data={formData}
            onChange={handleFieldChange}
            errors={errors}
          />
        );
      case 5:
        return (
          <FileUploadSection
            data={formData}
            onChange={handleFieldChange}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-xl max-w-5xl w-full my-8">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Submit New Problem</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {showPreview ? 'Review your submission' : `Step ${currentStep} of ${totalSteps}: ${stepTitles?.[currentStep - 1]}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={isSubmitting}
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Progress Indicator */}
        {!showPreview && (
          <div className="px-6 pt-6">
            <FormProgressIndicator
              currentStep={currentStep}
              totalSteps={totalSteps}
              stepTitles={stepTitles}
              completedSections={Array.from({ length: currentStep - 1 }, (_, i) => i + 1)}
            />
          </div>
        )}

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {submitError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-900">Submission Error</h4>
                <p className="text-sm text-red-800 mt-1">{submitError}</p>
              </div>
            </div>
          )}
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-between rounded-b-lg">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={(currentStep === 1 && !showPreview) || isSubmitting}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Back
          </Button>

          <div className="flex items-center space-x-3">
            {showPreview ? (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                iconName={isSubmitting ? 'Loader' : 'Send'}
                iconPosition="right"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Problem'}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                iconName="ChevronRight"
                iconPosition="right"
              >
                {currentStep === totalSteps ? 'Review' : 'Next'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitProblemModal;